"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ExpertiseInEveryWire() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: subRef,
    isVisible: subVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const headlineWords = [{
    text: "Expertise",
    className: ""
  }, {
    text: "in",
    className: ""
  }, {
    text: "every",
    className: "text-[color:var(--chart-2)]"
  }, {
    text: "wire",
    className: ""
  }];
  return <section data-section-id="5130" id="expertise-in-every-wire" className="relative min-h-screen w-full overflow-hidden bg-background text-foreground">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} aria-hidden="true" />

      {}
      <div className="absolute left-4 top-32 z-20 md:left-8 md:top-40">
        <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground" style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)"
      }}>
          01 / INTRO
        </span>
      </div>

      <div className="relative mx-auto grid min-h-screen w-full max-w-[1440px] grid-cols-1 gap-8 px-6 py-24 md:grid-cols-12 md:px-12 md:py-20 lg:gap-12">
        {}
        <div className="relative z-10 col-span-1 flex flex-col justify-center md:col-span-7 md:col-start-2 lg:col-span-5 lg:col-start-2">
          {}
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-8 right-0 select-none font-serif text-[18rem] leading-none text-muted/50 md:text-[22rem] lg:text-[26rem]">
            01
          </div>

          <div ref={headlineRef} className="relative">
            <h1 className="font-serif font-default text-foreground" style={{
            fontSize: "clamp(3.5rem, 9vw, 8rem)",
            lineHeight: "0.95",
            letterSpacing: "-0.025em",
            fontWeight: 800
          }}>
              {headlineWords.map((word, i) => <span key={i} data-index={i} className="mr-[0.25em] inline-block overflow-hidden align-bottom">
                  <span className={`inline-block transition-all duration-[800ms] ${word.className}`} style={{
                transitionTimingFunction: "cubic-bezier(0.215, 0.61, 0.355, 1)",
                transitionDelay: `${i * 80}ms`,
                transform: headlineVisible ? "translateY(0)" : "translateY(110%)",
                opacity: headlineVisible ? 1 : 0
              }}>
                    {word.text}
                  </span>
                </span>)}
            </h1>
          </div>

          <div ref={subRef} className="relative mt-8 max-w-md transition-all duration-[900ms]" style={{
          transitionDelay: "500ms",
          transform: subVisible ? "translateY(0)" : "translateY(20px)",
          opacity: subVisible ? 1 : 0
        }}>
            <p className="font-sans text-lg leading-relaxed text-muted-foreground md:text-[18px]">
              Professional electrical contracting services for residential and commercial projects, delivered with precision and safety.
            </p>

            <div className="mt-10 flex flex-col items-start gap-6 sm:flex-row sm:items-center">
              <Link to="/LetsTalkPage">
                <Button size="lg" className="group h-12 bg-primary px-7 text-base font-bold text-primary-foreground shadow-md hover:bg-primary/90">
                  Request Service Now
                  <ArrowRight className="ml-1 size-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </Link>

              <Link to="/OurExpertisePage" className="group relative inline-flex items-center font-sans text-base font-medium text-foreground">
                <span className="relative">
                  View our services
                  <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-100 bg-foreground transition-transform duration-500 ease-out group-hover:scale-x-0" />
                  <span className="absolute -bottom-1 left-0 h-px w-full origin-right scale-x-0 bg-foreground transition-transform duration-500 ease-out delay-150 group-hover:scale-x-100 group-hover:origin-left" />
                </span>
              </Link>
            </div>
          </div>
        </div>

        {}
        <div className="relative col-span-1 md:col-span-5 lg:col-span-6 lg:col-start-7">
          <div ref={imageRef} className="relative h-[60vh] w-full transition-all duration-[1200ms] md:absolute md:-top-12 md:bottom-[-6rem] md:h-auto" style={{
          transitionTimingFunction: "cubic-bezier(0.215, 0.61, 0.355, 1)",
          transitionDelay: "300ms",
          transform: imageVisible ? "translateY(0) scale(1)" : "translateY(40px) scale(0.98)",
          opacity: imageVisible ? 1 : 0
        }}>
            <div className="relative h-full w-full overflow-hidden shadow-lg">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700070/img/electrician_at_work.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700070/img/electrician_at_work.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700070/img/electrician_at_work.png-wvc-sizes" alt="Licensed electrician working on a complex electrical panel" loading="lazy" decoding="async" className="h-full w-full object-cover" style={{
              aspectRatio: "4/5"
            }} />

              {}
              <div className="pointer-events-none absolute inset-0" style={{
              background: "linear-gradient(180deg, transparent 60%, hsl(215 60% 12% / 0.4) 100%)"
            }} aria-hidden="true" />

              {}
              <div className="absolute bottom-5 left-5 z-10">
                <div className="inline-flex items-center gap-2 bg-secondary px-3 py-2 shadow-md backdrop-blur-sm">
                  <span className="block size-1.5 bg-primary" aria-hidden="true" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-white">
                    CERTIFIED · LICENSED · INSURED
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-border" aria-hidden="true" />
    </section>;
}